import { ArrowDownToLine, Check, Copy, Landmark, X } from "lucide-react";
import { cloneElement, useEffect, useRef, useState, type ReactElement } from "react";
import { ApiError, apiRequest } from "../lib/api";
import type { ApiEnvelope, FundingInstructions, WalletTransaction } from "../types";
import { money } from "../lib/format";
import { Modal } from "./Modal";
import { OperationDialog } from "./OperationDialog";
import { Spinner } from "./Spinner";

/**
 * Error codes the funding-instructions route returns while the Kuspit contract is opening but the
 * CLABE has not been issued yet. These are transient: the contract-open is slow (30s+) and can 502
 * once before a recovery re-login discovers the CLABE, so the right response is to wait and poll,
 * not to surface a failure. See apps/api/src/wallet/routes.ts (funding-instructions).
 */
const PENDING_CODES = new Set(["WALLET_NOT_READY", "FUNDING_ACCOUNT_PENDING"]);
/** Poll cadence and ceiling while the CLABE is provisioning (~36s of patience before we give up). */
const POLL_INTERVAL_MS = 3000;
const MAX_POLLS = 12;

/**
 * "Aportar" (Desktop · Wallet Libre · Aportar panel): a frosted panel over the dimmed dashboard with
 * the wallet's CLABE. Funding is an inbound SPEI transfer to that CLABE — Kuspit publishes no deposit
 * service — so the CLABE is the real rail. Against the mock, a test top-up is offered underneath.
 */
export function ContributeDialog({ walletId, trigger, onCompleted }: {
  walletId: string;
  trigger: ReactElement<{ onClick?: () => void }>;
  onCompleted(transaction: WalletTransaction): void;
}) {
  const [open, setOpen] = useState(false);
  const [funding, setFunding] = useState<FundingInstructions | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const pollCount = useRef(0);

  const stopPolling = () => {
    clearTimeout(pollTimer.current);
    pollTimer.current = undefined;
  };

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const payload = await apiRequest<ApiEnvelope<{ funding: FundingInstructions }>>(`/api/v1/wallets/${walletId}/funding-instructions`);
      setFunding(payload.data.funding);
      setProvisioning(false);
      pollCount.current = 0;
    } catch (cause) {
      const code = cause instanceof ApiError ? cause.code : "";
      if (PENDING_CODES.has(code) && pollCount.current < MAX_POLLS) {
        // The contract is still opening at Kuspit. Show a calm "activating" state and try again
        // shortly instead of an error the customer has to interpret and retry by hand.
        pollCount.current += 1;
        setProvisioning(true);
        setError(null);
        stopPolling();
        pollTimer.current = setTimeout(() => void load(), POLL_INTERVAL_MS);
      } else if (PENDING_CODES.has(code)) {
        // Still not ready after MAX_POLLS — Kuspit is unusually slow. Let the customer wait and retry.
        setProvisioning(false);
        setError("Tu wallet se está activando con Kuspit. Puede tardar un momento; inténtalo de nuevo en unos segundos.");
      } else {
        setProvisioning(false);
        setError(cause instanceof ApiError || cause instanceof Error ? cause.message : "No pudimos obtener la CLABE. Intenta de nuevo.");
      }
    } finally { setLoading(false); }
  };

  useEffect(() => () => { clearTimeout(copiedTimer.current); stopPolling(); }, []);

  const changeOpen = (next: boolean) => {
    setOpen(next);
    // Stop polling in the background once the panel is dismissed.
    if (!next) stopPolling();
  };

  const openDialog = () => {
    setOpen(true); setCopied(false); setCopyFailed(false);
    if (!funding) { pollCount.current = 0; void load(); }
  };

  const retry = () => { pollCount.current = 0; void load(); };

  const copy = async () => {
    if (!funding) return;
    try {
      await navigator.clipboard.writeText(funding.clabe);
      setCopied(true); setCopyFailed(false);
      clearTimeout(copiedTimer.current);
      copiedTimer.current = setTimeout(() => setCopied(false), 1600);
    } catch { setCopyFailed(true); }
  };

  return (
    <>
      {cloneElement(trigger, { onClick: openDialog })}
      <Modal open={open} label="Aportar fondos" onOpenChange={changeOpen}>
        <div className="dialog-content frosted">
          <div className="dialog-icon deposit"><Landmark aria-hidden="true" /></div>
          <h2>Aporta a tu viaje</h2>
          <p>Transfiere el monto que quieras a tu Wallet.</p>
          {provisioning && !funding && (
            <div className="dialog-loading" role="status">
              <Spinner label="Activando tu wallet" />
              <span>Estamos activando tu wallet con Kuspit. Tu CLABE aparecerá en unos segundos…</span>
            </div>
          )}
          {loading && !provisioning && !funding && <div className="dialog-loading"><Spinner label="Obteniendo CLABE" /><span>Obteniendo tu CLABE</span></div>}
          {!loading && !provisioning && error && (
            <div className="dialog-form">
              <div className="form-message" role="status">{error}</div>
              <button type="button" className="secondary-button full" onClick={retry}>Reintentar</button>
            </div>
          )}
          {!loading && funding && (
            <div className="dialog-form">
              <div className="field-block">
                <span className="field-label" id="funding-clabe-label">CLABE interbancaria</span>
                <div className="clabe-display" aria-labelledby="funding-clabe-label" translate="no">{funding.clabe}</div>
                {funding.beneficiaryName && <span className="field-hint">Beneficiario: {funding.beneficiaryName} · {funding.currency}</span>}
              </div>
              <button type="button" className="primary-button full" onClick={() => void copy()}>
                {copied ? <><Check size={17} aria-hidden="true" /> CLABE copiada</> : <><Copy size={17} aria-hidden="true" /> Copiar CLABE</>}
              </button>
              {copyFailed && <span className="field-hint" role="status">No pudimos copiarla automáticamente. Selecciona la CLABE y cópiala manualmente.</span>}
              {funding.depositLimit?.applies && (
                <div className="form-message" role="status">
                  {funding.depositLimit.remainingMxn > 0
                    ? <>Mientras Kuspit completa tu expediente, este mes puedes recibir hasta <span translate="no">{money.format(funding.depositLimit.remainingMxn)}</span> más (límite mensual de <span translate="no">{funding.depositLimit.limitUdi.toLocaleString("es-MX")} UDIs ≈ {money.format(funding.depositLimit.limitMxn)}</span>). Una transferencia mayor sería devuelta por Kuspit.</>
                    : <>Este mes ya alcanzaste el límite de <span translate="no">{money.format(funding.depositLimit.limitMxn)}</span> (<span translate="no">{funding.depositLimit.limitUdi.toLocaleString("es-MX")} UDIs</span>) que Kuspit permite mientras tu expediente está incompleto. Una transferencia adicional sería devuelta; completa tu verificación para aportar sin límite.</>}
                </div>
              )}
              <p className="funding-quote">Cada depósito te acerca a tu destino.</p>
            </div>
          )}
          {/* Instructed top-up: only the mock provider settles it. Live Kuspit publishes no deposit
              service, so outside the mock the CLABE above is the only way to fund the wallet. */}
          {funding?.instructedDeposits && <div className="dialog-secondary">
            <OperationDialog
              walletId={walletId}
              type="deposit"
              depositLimit={funding?.depositLimit ?? null}
              onCompleted={(transaction) => { onCompleted(transaction); setOpen(false); }}
              trigger={<button type="button" className="link-button"><ArrowDownToLine size={14} aria-hidden="true" /> Agregar fondos</button>}
            />
          </div>}
          <button type="button" className="dialog-close" onClick={() => changeOpen(false)} aria-label="Cerrar"><X size={20} aria-hidden="true" /></button>
        </div>
      </Modal>
    </>
  );
}
